import React, { useContext } from 'react'
import { DownloadCSVButton } from '../../../../../components/common/download-csv-button/DownloadCSVButton'
import {
  useScormTopicVersionAggregatedResultsQuery,
  ScormTopicVersionAggregatedResultsDocument
} from './SCORMQuizAggregateResults.codegen'
import '../quizResults/QuizResults.less'
import { t, Trans } from '@lingui/macro'
import {
  percentageOfTotalFormatter,
  QuizOption
} from '../quizOption/QuizOption'
import Loader from 'front-end/src/components/common/loader/Loader'
import { Box } from '@chakra-ui/react'
import { AnalyticsContext } from '../../../../analytics/AnalyticsContext'

export const SCORMQuizAggregateResults = ({
  topicId,
  title
}: {
  topicId: number
  title: string
}) => {
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const { data, loading } = useScormTopicVersionAggregatedResultsQuery({
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      scormResourceFilter: {
        topicId
      }
    }
  })

  if (loading) {
    return <Loader />
  }

  if (!data?.analytics) {
    return null
  }

  if (data?.analytics.scorm.resourcesAggregatedQuizResults.length === 0) {
    return (
      <div className="workbook-quizzes">
        <p className="AnalyticsPage__no-data">
          <Trans>No data available</Trans>
        </p>
      </div>
    )
  }

  return (
    <div className="workbook-quizzes">
      {data?.analytics.scorm.resourcesAggregatedQuizResults.map(
        (publishedQuiz, i) => {
          const { responsesBreakdown, options } = publishedQuiz
          const totalResponses =
            responsesBreakdown.correct + responsesBreakdown.incorrect

          return (
            <div className="QuizResult" key={`QuizResult-${i}`}>
              <div className="QuizResult__content">
                <div className="QuizResult__header">
                  <Trans>Question {i + 1}</Trans>{' '}
                </div>
                <div
                  className="page-content  page-content--analytics"
                  dangerouslySetInnerHTML={{
                    __html: publishedQuiz.content as string
                  }}
                />
                <div className="QuizResult__answer">
                  {options &&
                    options.map((option, i) => (
                      <QuizOption
                        key={i}
                        responsesBreakdown={responsesBreakdown}
                        option={option}
                        isCorrect={publishedQuiz.response_is_correct}
                        isOpenQuiz={false}
                        totalResponses={totalResponses}
                        viewAsStudent={false}
                      />
                    ))}
                </div>
              </div>

              <div className="QuizResult__assessment">
                <>
                  <div className="QuizResult__total">
                    {percentageOfTotalFormatter(
                      responsesBreakdown.correct,
                      totalResponses
                    )}
                  </div>

                  <span className="small">
                    {totalResponses === 0
                      ? t`Nobody answered this question`
                      : t`${responsesBreakdown.correct} of ${totalResponses} answered correctly`}
                  </span>
                </>
              </div>
            </div>
          )
        }
      )}
      <Box p="30px">
        <DownloadCSVButton
          fileName={`${title}-quiz-results`}
          dataPath={'data.analytics.scorm.resourcesAggregatedQuizResults'}
          queryOptions={() => ({
            variables: {
              mainAnalyticsFilter: filterAsQueryVariables,
              scormResourceFilter: {
                topicId
              }
            },
            query: ScormTopicVersionAggregatedResultsDocument
          })}
          prepareData={(data) => {
            const res: any[] = []
            data.forEach((question, i) => {
              const totalResponses =
                question.responsesBreakdown.correct +
                question.responsesBreakdown.incorrect
              res.push(
                {
                  A: '',
                  B: '',
                  C: '',
                  D: ''
                },
                {
                  A: `Q ${i + 1}. ${question.content}`
                },
                { A: '' },
                {
                  A: 'Answered Options',
                  B: 'Response Count',
                  C: 'Response Percent',
                  D: 'Response'
                }
              )

              question.options.forEach((option) => {
                res.push({
                  A: option.content,
                  B: option.clickRate.clicks,
                  C: `${(
                    (option.clickRate.clicks / totalResponses) *
                    100
                  ).toFixed()}%`,
                  D: option.response_is_correct ? 'Correct' : 'Incorrect'
                })
              })
            })
            return res
          }}
        />
      </Box>
    </div>
  )
}
